import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CodeArtifact, CodeSession, FinalActionChoice } from './types';
import { RequestTab } from './RequestTab';
import { SessionTab } from './SessionTab';
import { ArtifactsTab } from './ArtifactsTab';
type TabId = 'request' | 'session' | 'artifacts';
interface Props {
  session: CodeSession | null;
  artifacts: CodeArtifact[];
  breadcrumb: string;
  onClose: () => void;
  onInterrupt: () => void;
  onSendMessage: (content: string) => void;
  onPickChoice: (choice: FinalActionChoice) => void;
  onAnswerHitl: (artifactId: string, answer: string) => void;
}
export function SessionSheet({
  session,
  artifacts,
  breadcrumb,
  onClose,
  onInterrupt,
  onSendMessage,
  onPickChoice,
  onAnswerHitl
}: Props) {
  const [tab, setTab] = useState<TabId>('session');
  useEffect(() => {
    if (session) setTab('session');
  }, [session?.id]);
  useEffect(() => {
    if (!session) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [session, onClose]);
  return <AnimatePresence data-id="element-549">
      {session && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 z-[70]" onClick={onClose} data-id="element-550" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 280
      }} drag="y" dragConstraints={{
        top: 0,
        bottom: 0
      }} dragElastic={0.2} onDragEnd={(_, info) => {
        if (info.offset.y > 120) onClose();
      }} className="fixed inset-x-0 bottom-0 z-[71] bg-[#141418] rounded-t-[20px] border-t border-white/[0.06] shadow-2xl" style={{
        height: '90vh'
      }} data-id="element-551">
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1" data-id="element-552">
              <div className="w-10 h-1 rounded-full bg-[#8a8680]/30" data-id="element-553" />
            </div>

            {/* Tab bar */}
            <div className="flex items-center border-b border-white/[0.04] px-4" data-id="element-554">
              {(['request', 'session', 'artifacts'] as const).map(t => {
            const isActive = tab === t;
            return <button key={t} onClick={() => setTab(t)} className="relative py-3 px-4 text-[13px] capitalize transition-colors" style={{
              color: isActive ? '#e8e4df' : '#8a8680'
            }} data-id="element-555">
                    {t}
                    {isActive && <motion.div layoutId="code-sheet-tab" className="absolute inset-x-3 bottom-0 h-[2px] bg-[#4a9a9a]" transition={{
                type: 'spring',
                damping: 28,
                stiffness: 300
              }} data-id="element-556" />}
                  </button>;
          })}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-hidden" style={{
          height: 'calc(90vh - 56px)'
        }} data-id="element-557">
              {tab === 'request' && <div className="h-full overflow-y-auto" data-id="element-558"><RequestTab session={session} breadcrumb={breadcrumb} data-id="element-559" /></div>}
              {tab === 'session' && <SessionTab session={session} onInterrupt={onInterrupt} onSendMessage={onSendMessage} onPickChoice={onPickChoice} data-id="element-560" />}
              {tab === 'artifacts' && <div className="h-full overflow-y-auto" data-id="element-561">
                  <ArtifactsTab artifacts={artifacts} onAnswerHitl={onAnswerHitl} onJumpToSessionTab={() => setTab('session')} data-id="element-562" />
                </div>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}